import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import Select from "react-select";
import useApi from "../../../../hooks/useApi";
import { useAuth } from "../../../../hooks/useAuth";
import {
  getRestaurantUsers,
  closeRestaurantManagerAccount,
} from "../../../../services/userService";
import { deleteRestaurant } from "../../../../services/restaurantService";
import Alert from "../../../utils/Alert";
import Button from "../../../buttons/Button";
import DropdownIndicator from "../../../inputs/DropdownIndicator";
import { stopControllingRestaurant } from "../../../../redux/slices/adminSlice";
import { useDispatch } from "react-redux";
import useLangNavigate from "../../../../hooks/useLangNavigate";

const DangerZone = ({ tKey = "dashboard.settings.dangerZone." }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [hasToSelectNewManager, setHasToSelectNewManager] = useState(false);
  const [hasToConfirmCloseAccount, setHasToConfirmCloseAccount] =
    useState(false);
  const { logout } = useAuth();
  const dispatch = useDispatch();
  const { request: getUsersRequest } = useApi(getRestaurantUsers);
  const { request: deleteRestaurantRequest } = useApi(deleteRestaurant);
  const { request: closeRestaurantManagerAccountRequest } = useApi(
    closeRestaurantManagerAccount
  );
  const user = useSelector((state) => state.user);
  const { isOffoodAdmin } = useAuth();
  const controlledRestaurantId = useSelector(
    (state) => state.admin.controlledRestaurantId
  );
  const navigate = useLangNavigate();

  useEffect(() => {
    getUsersRequest(user.restaurantId)
      .then((response) => {
        setEmployees(
          response.data.employees.filter(
            (employee) =>
              employee.userId !== user.userId && employee.isValidated
          )
        );
      })
      .catch((error) => {
        console.log(error);
        setEmployees([]);
      });
  }, [user.restaurantId, user.userId, getUsersRequest]);

  const attemptCloseRestaurant = useCallback(
    async (restaurantId) => {
      await deleteRestaurantRequest(restaurantId)
        .then(() => {
          if (!isOffoodAdmin) {
            logout();
          } else {
            dispatch(stopControllingRestaurant());
            navigate("/dashboard");
          }
          toast.success(t(`${tKey}restaurantDeletion.success`));
        })
        .catch((error) => {
          toast.error(t(`${tKey}restaurantDeletion.error`));
          console.log(error);
        });
    },
    [
      deleteRestaurantRequest,
      t,
      tKey,
      logout,
      dispatch,
      navigate,
      isOffoodAdmin,
    ]
  );

  const handleCloseRestaurant = useCallback(() => {
    attemptCloseRestaurant(controlledRestaurantId || user.restaurantId);
  }, [controlledRestaurantId, user.restaurantId, attemptCloseRestaurant]);

  const attemptCloseAccount = useCallback(async () => {
    if (!selectedEmployee) return;
    await closeRestaurantManagerAccountRequest(
      controlledRestaurantId || user.restaurantId,
      {
        newManagerId: selectedEmployee.userId,
        oldManagerId: controlledRestaurantId ? null : user.userId,
      }
    )
      .then(() => {
        if (!isOffoodAdmin) {
          logout();
        } else {
          dispatch(stopControllingRestaurant());
          navigate("/dashboard");
        }
        toast.success(t(`${tKey}accountDeletion.success`));
      })
      .catch((error) => {
        toast.error(error.response.data.message);
        console.log(error);
      });
  }, [
    selectedEmployee,
    user.restaurantId,
    user.userId,
    controlledRestaurantId,
    closeRestaurantManagerAccountRequest,
    logout,
    isOffoodAdmin,
    t,
    tKey,
    dispatch,
    navigate,
  ]);

  return (
    <>
      <div className="dashboard-content flex column gap-2">
        <div>
          <Alert variant="danger">
            <h4 className="my-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {t(`${tKey}restaurantDeletion.alert.title`)}
            </h4>
            <p>{t(`${tKey}restaurantDeletion.alert.text`)}</p>
            <Button
              variant={hasToConfirmCloseAccount ? "secondary" : "danger"}
              size="sm"
              className="mt-2"
              onClick={() =>
                setHasToConfirmCloseAccount(!hasToConfirmCloseAccount)
              }
            >
              {t(
                hasToConfirmCloseAccount
                  ? `utils.common.cancel`
                  : `${tKey}restaurantDeletion.button`
              )}
            </Button>
            {hasToConfirmCloseAccount && (
              <Button
                size="sm"
                className="mt-2"
                onClick={handleCloseRestaurant}
              >
                {t(`${tKey}restaurantDeletion.lastButton`)}
              </Button>
            )}
          </Alert>
        </div>
        <div>
          <Alert variant="danger">
            <h4 className="my-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {t(`${tKey}accountDeletion.alert.title`)}
            </h4>
            <p>{t(`${tKey}accountDeletion.alert.text`)}</p>
            <div className="flex align-center gap-1">
              <Button
                variant={hasToSelectNewManager ? "secondary" : "danger"}
                size="sm"
                className="mt-2"
                onClick={() => setHasToSelectNewManager(!hasToSelectNewManager)}
              >
                {t(
                  hasToSelectNewManager
                    ? `utils.common.cancel`
                    : `${tKey}accountDeletion.button`
                )}
              </Button>
              {hasToSelectNewManager && (
                <Select
                  className="react-custom-select text-dark col-3"
                  classNamePrefix="react-select"
                  options={employees.map((employee) => ({
                    value: employee,
                    label: `${employee.firstname} ${employee.lastname}`,
                  }))}
                  onChange={(selected) => setSelectedEmployee(selected.value)}
                  components={{ DropdownIndicator }}
                />
              )}
            </div>
            {hasToSelectNewManager && (
              <Button
                size="sm"
                disabled={!selectedEmployee}
                className="mt-2"
                onClick={attemptCloseAccount}
              >
                {t(`${tKey}restaurantDeletion.lastButton`)}
              </Button>
            )}
          </Alert>
        </div>
      </div>
    </>
  );
};

export default DangerZone;
